import { collection, query, where, getDocs } from 'firebase/firestore';
import { getSitesByOrganization } from '../services/siteService';

/**
 * Generate CSV content with headers and metadata
 * @param {Array} headers - Column headers
 * @param {Array} rows - Data rows
 * @param {Object} metadata - Additional metadata for the CSV
 * @returns {string} CSV content
 */
const generateCSVContent = (headers, rows, metadata = {}) => {
  const csvRows = [];
  
  // Add metadata header
  csvRows.push(`Export generated via CrisisCompass C3 | v1.0`);
  csvRows.push(`Export time: ${new Date().toLocaleString()}`);
  if (metadata.orgName) {
    csvRows.push(`Organization: ${metadata.orgName}`);
  }
  if (metadata.exportType) {
    csvRows.push(`Export type: ${metadata.exportType}`);
  }
  csvRows.push(''); // Empty line
  
  // Add headers
  csvRows.push(headers.join(','));
  
  // Add data rows
  rows.forEach(row => {
    const csvRow = row.map(field => {
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const fieldStr = String(field || '');
      if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
        return `"${fieldStr.replace(/"/g, '""')}"`;
      }
      return fieldStr;
    }).join(',');
    csvRows.push(csvRow);
  });
  
  return csvRows.join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - The CSV content
 * @param {string} filename - The filename
 */
const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate filename for dashboard exports
 * @param {string} orgName - The organization name
 * @param {string} exportType - 'SiteStatus' or 'StaffStatus'
 * @returns {string} Formatted filename
 */
const generateDashboardFilename = (orgName, exportType) => {
  // Sanitize organization name
  const sanitizedOrgName = orgName.replace(/[^a-zA-Z0-9]/g, '_');
  
  // Generate timestamp
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
  const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
  
  return `${sanitizedOrgName}_Dashboard_Export${exportType}_${dateStr}_${timeStr}.csv`;
};

/**
 * Export organization-wide site status to CSV
 * @param {string} orgId - Organization ID
 * @param {string} orgName - Organization name for filename
 * @returns {Promise<string>} Filename of exported file
 */
export const exportDashboardSiteStatus = async (orgId, orgName = 'Organization') => {
  try {
    // Get all sites for the organization
    const allSites = await getSitesByOrganization(orgId);
    
    // CSV Headers
    const headers = [
      'Site Name',
      'Site Type',
      'Location',
      'Address',
      'State/Province',
      'Country',
      'Status',
      'Last Updated',
      'Site Manager',
      'Manager Contact',
      'Latitude',
      'Longitude'
    ];
    
    // Convert sites to CSV rows
    const csvRows = allSites.map(site => [
      site.site_name || '',
      site.site_type || '',
      `${site.suburb || ''}, ${site.state || ''}`.replace(/^,\s*/, '').replace(/,\s*$/, ''),
      `${site.address_line_1 || ''} ${site.address_line_2 || ''}`.trim(),
      site.state || '',
      site.country || '',
      site.status || 'unknown',
      site.updated_at ? new Date(site.updated_at.seconds * 1000).toLocaleString() : '',
      site.site_manager_name || '',
      site.site_manager_mobile || site.site_manager_email || '',
      site.latitude || '',
      site.longitude || ''
    ]);
    
    // Generate CSV content
    const csvContent = generateCSVContent(headers, csvRows, {
      orgName,
      exportType: 'Organization-wide Site Status'
    });
    
    // Generate filename and download
    const filename = generateDashboardFilename(orgName, 'SiteStatus');
    downloadCSV(csvContent, filename);
    
    console.log(`✅ Exported ${allSites.length} sites to ${filename}`);
    return filename;
    
  } catch (error) {
    console.error('Error exporting site status:', error);
    throw error;
  }
};

/**
 * Export organization-wide staff status to CSV
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} orgName - Organization name for filename
 * @returns {Promise<string>} Filename of exported file
 */
export const exportDashboardStaffStatus = async (db, orgId, orgName = 'Organization') => {
  try {
    // Query all staff status for this organization (across all incidents)
    const staffRef = collection(db, 'c3_staff_status');
    const q = query(
      staffRef,
      where('orgId', '==', orgId)
    );
    
    const snapshot = await getDocs(q);
    const staffList = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // CSV Headers
    const headers = [
      'Staff Name',
      'Contact Number',
      'Incident ID',
      'Assigned Location',
      'Current Status',
      'Last Update Method',
      'Last Updated',
      'Notes',
      'Emergency Contact',
      'Recorded By'
    ];
    
    // Convert staff to CSV rows
    const csvRows = staffList.map(staff => [
      staff.fullName || '',
      [staff.email, staff.phone].filter(Boolean).join(' | ') || staff.contactInfo || staff.phoneNumber || '',
      staff.incidentId || '',
      staff.location || '',
      staff.status || 'unaccounted',
      staff.createdVia === 'sms_reply' ? 'SMS' : 
        staff.manualEntry ? 'Manual' : 'System',
      staff.lastUpdated ? new Date(staff.lastUpdated.seconds * 1000).toLocaleString() : '',
      staff.notes || '',
      staff.emergencyContact || '',
      staff.createdByName || staff.statusChangedByName || ''
    ]);
    
    // Generate CSV content
    const csvContent = generateCSVContent(headers, csvRows, {
      orgName,
      exportType: 'Organization-wide Staff Status'
    });
    
    // Generate filename and download
    const filename = generateDashboardFilename(orgName, 'StaffStatus');
    downloadCSV(csvContent, filename);
    
    console.log(`✅ Exported ${staffList.length} staff members to ${filename}`);
    return filename;
    
  } catch (error) {
    console.error('Error exporting staff status:', error);
    throw error;
  }
}; 